import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@repo/ui/components/button';
import { FormError } from '@repo/ui/components/form-error';
import { Input } from '@repo/ui/components/input';
import { Label } from '@repo/ui/components/label';
import { Textarea } from '@repo/ui/components/textarea';
import { Loader2 } from 'lucide-react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import type { WishlistItem } from '@/hooks/queries/use-relationship';

const wishlistItemSchema = z.object({
  title: z.string().min(1, 'O título é obrigatório'),
  description: z.string().optional(),
  link: z
    .string()
    .optional()
    .refine(
      (value) => {
        if (!value || value.trim() === '') {
          return true;
        }
        try {
          new URL(value);
          return true;
        } catch {
          return false;
        }
      },
      { message: 'Digite uma URL válida' },
    ),
});

export type WishlistItemFormValues = z.infer<typeof wishlistItemSchema>;

interface WishlistItemFormProps {
  item?: WishlistItem;
  onSubmit: (data: WishlistItemFormValues) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

export function WishlistItemForm({
  item,
  onSubmit,
  onCancel,
  isLoading = false,
}: WishlistItemFormProps) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<WishlistItemFormValues>({
    resolver: zodResolver(wishlistItemSchema),
    defaultValues: {
      title: item?.title ?? '',
      description: item?.description ?? '',
      link: item?.link ?? '',
    },
  });

  useEffect(() => {
    if (item) {
      reset({
        title: item.title,
        description: item.description ?? '',
        link: item.link ?? '',
      });
    } else {
      reset({
        title: '',
        description: '',
        link: '',
      });
    }
  }, [item, reset]);

  const handleFormSubmit = (data: WishlistItemFormValues) => {
    onSubmit({
      title: data.title,
      description: data.description || undefined,
      link: data.link || undefined,
    });
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="title">Título *</Label>
        <Input
          id="title"
          placeholder="O que você deseja?"
          disabled={isLoading}
          {...register('title')}
        />
        <FormError message={errors.title?.message} />
      </div>

      <div className="space-y-2">
        <Label htmlFor="description">Descrição</Label>
        <Textarea
          id="description"
          placeholder="Detalhes adicionais (tamanho, cor, modelo...)"
          rows={3}
          disabled={isLoading}
          {...register('description')}
        />
        <FormError message={errors.description?.message} />
      </div>

      <div className="space-y-2">
        <Label htmlFor="link">Link</Label>
        <Input
          id="link"
          type="url"
          placeholder="https://exemplo.com/produto"
          disabled={isLoading}
          {...register('link')}
        />
        <FormError message={errors.link?.message} />
      </div>

      <div className="flex gap-2 pt-4">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isLoading}
          className="flex-1"
        >
          Cancelar
        </Button>
        <Button type="submit" disabled={isLoading} className="flex-1">
          {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {item ? 'Salvar' : 'Adicionar'}
        </Button>
      </div>
    </form>
  );
}
